import { participationErrorKey } from '../../shared/utils/participation-error';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Event, EventRecurringType } from "../../shared/models/event";
import { EventService } from "../../shared/services/event.service";
import { PlayerEventInfoService } from "../../shared/services/playereventinfo.service";
import { PlayerService } from "../../shared/services/player.service";
import { PlaySpace } from "../../shared/models/playSpace";
import { PlayerEventInfo, PlayerEventInfoStatus, TeamMemberType } from "../../shared/models/playerEventInfo";
import { Player } from "app/shared/models/player";
import { Chat } from "../../shared/models/chat";
import { ChatService } from "../../shared/services/chat.service";
import { AuthenticatedRealtimeService } from "../../shared/services/authenticated-realtime.service";
import { Router, ActivatedRoute } from "@angular/router";
import { PlaySpaceService } from "../../shared/services/playspace.service";
import { BehaviorSubject, forkJoin, of, Subscription } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DelayedSpinnerService } from "../../shared/services/delayed-spinner.service";
import { TranslateService } from '@ngx-translate/core';
import { PendingRatingReview } from "../../shared/models/ratingReview";
import { AuthService } from "@auth0/auth0-angular";
import { AnalyticsService } from "../../shared/services/analytics.service";
import { AuthRedirectService } from "../../shared/services/auth-redirect.service";
import { DialogService } from "../../core/dialog/dialog.service";

@Component({
    selector: "event-list",
    templateUrl: "./event-list.component.html",
    styleUrls: ["../event.component.css", "./event-list.component.css"],
    providers: [],
    standalone: false
})
export class EventListComponent implements OnInit, OnDestroy {
  @Output() invokeHelpTab = new EventEmitter<any>();

  public events: Event[] = [];

  public allEvents: Event[] = [];
  public myEvents: Event[] = [];
  public sosEvents: Event[] = [];

  public creatingNewEvent: boolean = false;

  public isMyMatchesClicked: boolean = true;
  public isAllMatchesClicked: boolean = false;
  public isSosMatchesClicked: boolean = false;

  public selectedEvent: Event;
  public editSelectedEvent: boolean = false;

  public isCurrentPlayerManager: boolean;

  public currentPlayer: Player;
  public teamMemberType: TeamMemberType = new TeamMemberType();

  public playerEventInfoStatus: PlayerEventInfoStatus = new PlayerEventInfoStatus();

  public loading = true;
  public showDelayedLoadingState = false;
  public readonly eventListReady$ = new BehaviorSubject<boolean>(false);

  public showInviteDialog: boolean = false;
  public showLeaveEventDialog: boolean = false;

  public sendInviteEmailEvent: Event;
  public eventForLeave: Event;

  public currentPlayerCity: string;

  public joinMessage: string;
  public discoveryLocationMissing: boolean = false;

  public sosEventsCount: number;
  public myEventsCount: number;
  public rating: number = 4;
  public pendingRatingReviews: PendingRatingReview[] = [];
  public selectedRatingReviewId: string;
  public ratingReviewErrorMsg: string;
  public ratingReviewLoading: boolean = false;
  public emailVerificationLoading: boolean = false;
  public emailVerificationCheckLoading: boolean = false;
  public emailVerificationMessage: string;
  public emailVerificationError: string;
  public authEmailVerified: boolean = null;
  public emailVerificationConfirmed = false;
  public chats: Chat[] = [];
  public listError = false;
  private stopHomeRealtime: () => void;
  private homeRefreshTimer: ReturnType<typeof setTimeout>;
  private listSubscription: Subscription;
  private destroyed = false;
  private listReading = false;
  private homeRefreshPending = false;
  private chatRevision = 0;
  private authSubscription: Subscription;
  private currentPlayerSubscription: Subscription;
  private loadingSpinnerTimeout: any;
  private readonly loadingSpinnerDelayMs = 300;
  //public sosEventsCount: number;


  constructor(
    public playerService: PlayerService,
    public eventService: EventService,
    private playSpaceService: PlaySpaceService,
    public router: Router,
    private route: ActivatedRoute,
    private spinner: DelayedSpinnerService,
    private translate: TranslateService,
    private auth: AuthService,
    private analytics: AnalyticsService,
    private authRedirect: AuthRedirectService,
    private dialogService: DialogService,
    private chatService: ChatService,
    private realtime: AuthenticatedRealtimeService,
    private roster: PlayerEventInfoService
  ) {}

  ngOnInit() {
    this.showLoadingSpinner(false);
    this.currentPlayer = this.playerService.getCurrentPlayer();
    this.currentPlayerCity = this.getCurrentPlayerCity();
    this.currentPlayerSubscription = this.playerService.currentPlayer$.subscribe((player) => {
      if (!player) {
        return;
      }
      const changed = this.currentPlayer?.id !== player.id || this.currentPlayer?.city !== player.city;
      this.currentPlayer = player;
      this.currentPlayerCity = this.getCurrentPlayerCity();
      this.applyEmailVerificationState(player);
      if (changed) this.queueHomeRefresh();
    });
    this.authSubscription = this.auth.user$.subscribe((profile) => {
      this.authEmailVerified = profile && profile.email_verified !== undefined ? profile.email_verified : null;
    });
    this.playerService.checkCity();

    const filter = localStorage.getItem("eventsFilter") || "myMatches";
    this.filterEvents(filter);
    this.stopHomeRealtime = this.realtime.subscribe(event => this.onHomeRealtime(event));

    const prepared = this.route.snapshot.data['home'];
    if (prepared && !prepared.error) {
      this.currentPlayer = prepared.player;
      this.chats = prepared.chats;
      this.discoveryLocationMissing = !prepared.locationAvailable;
      this.enrichAndApplyEvents(this.mergeEvents(prepared.myEvents, prepared.nearbyEvents), prepared.myEvents, prepared.nearbyEvents, false);
      this.getCurrentCityAndLocations();
      this.loadPendingRatingReviews();
      this.refreshEmailVerificationStatus();
    } else if (prepared?.error) {
      this.listError = true;
      this.finishEventListLoading();
    } else if (this.currentPlayer) {
      this.loadEventListScreenData();
      this.refreshCurrentPlayer();
    } else {
      this.refreshCurrentPlayerAndLoadEvents();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.listSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.stopHomeRealtime?.();
    clearTimeout(this.homeRefreshTimer);
    if (this.currentPlayerSubscription) {
      this.currentPlayerSubscription.unsubscribe();
    }
    this.hideLoadingSpinner();
  }

  @HostListener("window:focus")
  public onWindowFocus(): void {
    this.refreshEmailVerificationStatus();
    this.queueHomeRefresh();
  }

  @HostListener("document:visibilitychange")
  public onVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      this.refreshEmailVerificationStatus();
      this.queueHomeRefresh();
    }
  }

  public requestToJoin(event: Event): void {
    if (this.loading || this.attendanceSaving || event.active === false || !(event.access?.canRequestJoin ?? event.openToPublic) || event.cancelled || event.incompleteSetup
      || event.viewedByCandidate || event.viewedByPlayer || event.viewedByManager) return;
    this.attendanceSaving = true;
    this.spinner.show();
    this.eventService.requestToJoinEvent(event, this.currentPlayer).subscribe({
      next: () => {
        if (this.destroyed) return;
        this.attendanceSaving = false;
        this.getEventList(false, true);
      },
      error: () => {
        if (this.destroyed) return;
        this.attendanceSaving = false;
        this.spinner.hide();
        this.dialogService.alert({title: this.translate.instant('common.alert'), text: this.translate.instant('errors.generic')});
        if (this.homeRefreshPending) this.queueHomeRefresh();
      }
    });
  }

  public sendInviteEmail(inviteToNogoshEmailList: string): void {
    this.loading = true;
    this.showLoadingSpinner();
    this.eventService.invitePlayersToJoinNogosh(this.sendInviteEmailEvent, inviteToNogoshEmailList).subscribe(
      () => {
        this.dialogService.alert({
          title: this.translate.instant('common.alert'),
          text: this.translate.instant('errors.inviteInstructions')
        });

        this.hideLoadingSpinner();
        this.loading = false;
      },
      (res) => {
        console.log(res.error);
        this.hideLoadingSpinner();
        this.loading = false;
      }
    );

    this.showInviteDialog = false;
  }

  public dialogSendInviteEmail(event: Event): void {
    this.showInviteDialog = true;
    this.sendInviteEmailEvent = event;
  }

  public playerInitials(player: Partial<Player>): string {
    const displayName = this.playerDisplayName(player);
    if (!displayName) {
      return "?";
    }

    const parts = displayName.split(/[\s._-]+/).filter(Boolean);
    if (parts.length > 1) {
      return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
    }

    return displayName.slice(0, 2).toUpperCase();
  }

  public playerAvatarTone(player: Partial<Player>): string {
    const displayName = this.playerDisplayName(player);
    const hash = displayName.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return "avatar-initial-tone-" + (hash % 6);
  }

  private playerDisplayName(player: Partial<Player>): string {
    return player ? (player.nickName || player.name || player.email || "").trim() : "";
  }

  public createNewEvent(): void {
    if (this.creatingNewEvent || !this.currentPlayer) {
      return;
    }

    this.dialogService.alert({
      title: this.translate.instant("events.createTeamConfirmTitle"),
      text: this.translate.instant("events.createTeamConfirmMessage"),
      closeLabel: this.translate.instant("common.cancel"),
      actionLabel: this.translate.instant("events.createTeamConfirmAction"),
      action: () => this.createNewEventConfirmed()
    });
  }

  private createNewEventConfirmed(): void {
    if (this.creatingNewEvent || !this.currentPlayer) {
      return;
    }

    this.creatingNewEvent = true;
    this.loading = true;
    this.showLoadingSpinner();

    const newEvent = new Event();
    newEvent.name = this.translate.instant("events.newTeam");
    newEvent.originalEventId = "";
    newEvent.manager = this.currentPlayer;
    newEvent.active = true;
    newEvent.openToPublic = false;
    newEvent.playSpace = null;
    newEvent.locationName = null;
    newEvent.nextOccurrenceTimestamp = null;
    newEvent.nextOccurrenceTimestampZone = null;
    newEvent.playerEventInfos = Array<PlayerEventInfo>();
    newEvent.confirmedCount = 1;
    newEvent.maxPlayers = 10;
    newEvent.recurringType = new EventRecurringType().ONE_OFF;
    newEvent.incompleteSetup = true;

    this.eventService.createEvent(newEvent).subscribe(
      (event) => {
        event.originalEventId = event.originalEventId ? event.originalEventId : event.id;
        event.viewedByManager = true;
        event.playerEventInfos = event.playerEventInfos || [];
        localStorage.setItem("event", JSON.stringify(event));
        this.navigateAndResetScroll(["/event-players", false]);
        this.hideLoadingSpinner();
        this.loading = false;
        this.creatingNewEvent = false;
      },
      (error) => {
        console.log(error);
        this.hideLoadingSpinner();
        this.loading = false;
        this.creatingNewEvent = false;
      }
    );
  }

  public retryList(): void { this.getEventList(); }

  public trackHomeEvent(index: number, event: Event): string { return event.id; }

  public homeChat(event: Event): Chat {
    return this.chats.find(chat => chat.id === event.chat?.id || chat.entityId === event.id || chat.entityId === event.originalEventId);
  }

  public homeAction(event: Event, action: string): void {
    if (action === "join") { this.requestToJoin(event); return; }
    localStorage.setItem("event", JSON.stringify(event));
    if (action === "chat") {
      const chat = this.homeChat(event);
      if (chat?.id) this.router.navigate(["/messages", {chatId: chat.id}]);
      return;
    }
    if (action !== "overview") sessionStorage.setItem("matchHomeAction", JSON.stringify({ eventId: event.id, action }));
    if (event.viewedByManager || event.viewedByPlayer) this.eventPlayers(event);
    else this.eventDetail(event);
  }

  public attendanceSaving = false;
  public teamSaving = false;
  public onTeamSaving(saving: boolean): void {
    this.teamSaving = saving;
    if (saving) {
      this.listSubscription?.unsubscribe();
      this.listReading = false;
      clearTimeout(this.homeRefreshTimer);
      this.homeRefreshPending = true;
    } else this.queueHomeRefresh();
  }
  public homeAttendance(event: Event, status: string): void {
    const playerId = this.currentPlayer?.id;
    if (!['CONFIRMED', 'DECLINED'].includes(status)) return;
    const own = event.playerEventInfos?.find(item => item.player?.id === this.currentPlayer?.id);
    if (this.attendanceSaving || !own?.id || own.teamMemberType === 'CANDIDATE' || own.status === 'NOT_NOTIFIED' && !event.viewedByManager || event.cancelled || event.incompleteSetup || event.active === false || own.status === status) return;
    const save = () => {
      if (this.destroyed || this.playerService.getCurrentPlayer()?.id !== playerId) return;
      this.attendanceSaving = true;
      this.spinner.show();
      this.roster.update(Object.assign(new PlayerEventInfo(), own, {status}), this.currentPlayer).subscribe({
        next: () => { if (this.destroyed) return; this.attendanceSaving = false; this.getEventList(false); },
        error: error => {
          if (this.destroyed) return;
          this.attendanceSaving = false;
          this.spinner.hide();
          this.dialogService.alert({title: this.translate.instant('common.alert'), text: this.translate.instant(participationErrorKey(error))});
          if (error?.error?.code === 'PARTICIPATION_CHANGED') this.getEventList(false);
        }
      });
    };
    if (['CONFIRMED', 'DECLINED'].includes(own.status)) {
      this.dialogService.alert({title: this.translate.instant('events.changeAnswer'), text: this.translate.instant(status === 'CONFIRMED' ? 'events.confirmComingPrompt' : 'events.confirmNotComingPrompt'), closeLabel: this.translate.instant('common.cancel'), actionLabel: this.translate.instant('events.changeResponse'), action: save});
    } else save();
  }

  public homeCandidate(event: Event, decision: {player: PlayerEventInfo; decision: string}): void {
    const pei = decision.player;
    const playerId = this.currentPlayer?.id;
    if (this.attendanceSaving || !event.viewedByManager || event.manager?.id !== playerId ||
      event.active === false || event.cancelled || pei?.teamMemberType !== 'CANDIDATE' || !['FIRST_TEAM', 'RESERVE', 'REJECT'].includes(decision.decision)) return;
    const save = () => {
      if (this.destroyed || this.attendanceSaving || this.playerService.getCurrentPlayer()?.id !== playerId) return;
      this.attendanceSaving = true;
      this.spinner.show();
      const request = decision.decision === 'REJECT'
        ? this.roster.delete(pei.id)
        : this.roster.update(Object.assign(new PlayerEventInfo(), pei, {teamMemberType: decision.decision}), this.currentPlayer);
      request.subscribe({
        next: () => { if (this.destroyed) return; this.attendanceSaving = false; this.getEventList(false); },
        error: () => {
          if (this.destroyed) return;
          this.attendanceSaving = false;
          this.spinner.hide();
          this.dialogService.alert({title: this.translate.instant('common.alert'), text: this.translate.instant('errors.generic')});
          if (this.homeRefreshPending) this.queueHomeRefresh();
        }
      });
    };
    if (decision.decision === 'REJECT') {
      this.dialogService.alert({title: this.translate.instant('events.deny'), text: pei.player.nickName || pei.player.name,
        closeLabel: this.translate.instant('common.cancel'), actionLabel: this.translate.instant('events.deny'), destructive: true, action: save});
    } else save();
  }

  public homePlayer(event: Event, pei: PlayerEventInfo): void {
    this.openManagerProfile(event, pei.player);
  }

  public viewEvent(event: Event): void {
    localStorage.setItem("event", JSON.stringify(event));

    this.selectedEvent = event;
    this.editSelectedEvent = false;
    this.analytics.track("match_viewed", {
      screen: "matches_list",
      entityType: "event",
      entityId: event && event.id
    });

    this.navigateAndResetScroll(["/event-detail", this.editSelectedEvent]);
  }

  public eventPlayers(event: Event): void {
    localStorage.setItem("event", JSON.stringify(event));

    this.selectedEvent = event;
    this.analytics.track("match_viewed", {
      screen: "matches_list",
      entityType: "event",
      entityId: event && event.id
    });
    this.navigateAndResetScroll(["/event-players", false]);
  }
  public eventDetail(event: Event): void {
    localStorage.setItem("event", JSON.stringify(event));

    this.selectedEvent = event;
    this.analytics.track("match_viewed", {
      screen: "matches_list",
      entityType: "event",
      entityId: event && event.id
    });
    this.navigateAndResetScroll(["/event-detail", false]);
  }

  public openManagerProfile(event: Event, manager: Player, clickEvent?: MouseEvent): void {
    clickEvent?.preventDefault();
    clickEvent?.stopPropagation();

    if (!event || !manager || !manager.id) {
      return;
    }

    const managerPlayerEventInfo = this.findPlayerEventInfoForPlayer(event, manager.id);

    localStorage.removeItem("chatPlayer");
    localStorage.setItem("event", JSON.stringify(event));
    localStorage.setItem("eventForPlayerDetail", JSON.stringify(event));
    localStorage.setItem("openPlayerDetailForPlayerId", manager.id);
    if (event.id) {
      localStorage.setItem("openPlayerDetailForEventId", event.id);
    }
    if (managerPlayerEventInfo) {
      localStorage.setItem("peiForPlayerDetail", JSON.stringify(managerPlayerEventInfo));
    } else {
      localStorage.removeItem("peiForPlayerDetail");
    }

    this.analytics.track("profile_opened", {
      screen: "matches_list",
      entityType: "player",
      entityId: manager.id
    });
    this.navigateAndResetScroll(["/event-players", false]);
  }

  private findPlayerEventInfoForPlayer(event: Event, playerId: string): PlayerEventInfo {
    if (!event || !event.playerEventInfos || !playerId) {
      return null;
    }

    return event.playerEventInfos.find((playerEventInfo) =>
      playerEventInfo && playerEventInfo.player && playerEventInfo.player.id === playerId
    );
  }

  private navigateAndResetScroll(commands: any[]): void {
    const isMatchRoute = ['/event-players', '/event-detail'].includes(commands[0]);
    let eventId: string;
    try { eventId = isMatchRoute ? JSON.parse(localStorage.getItem('event') || 'null')?.id : null; } catch { eventId = null; }
    this.router.navigate(commands, eventId ? {queryParams: {eventId}} : {}).then((navigated) => {
      if (!navigated) {
        return;
      }

      window.setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 0);
    });
  }

  public get pendingRatingReview(): PendingRatingReview {
    if (!this.pendingRatingReviews || this.pendingRatingReviews.length === 0) {
      return null;
    }

    return this.pendingRatingReviews[0];
  }

  public openRatingReview(review: PendingRatingReview): void {
    this.ratingReviewErrorMsg = null;
    this.selectedRatingReviewId = review.id;
    this.analytics.track("rating_review_opened", {
      screen: "matches_list",
      entityType: "rating_review",
      entityId: review && review.id
    });
  }

  public closeRatingReview(): void {
    this.selectedRatingReviewId = null;
  }

  public ratingReviewCompleted(): void {
    this.selectedRatingReviewId = null;
    this.loadPendingRatingReviews();
  }

  public shouldShowEmailVerificationReminder(): boolean {
    return !this.emailVerificationCheckLoading && !this.emailVerificationConfirmed && !!this.currentPlayer &&
      (this.isPlayerEmailUnverified(this.currentPlayer) || this.authEmailVerified === false);
  }

  public sendVerificationEmail(): void {
    this.emailVerificationLoading = true;
    this.emailVerificationMessage = null;
    this.emailVerificationError = null;

    this.playerService.sendEmailVerification().subscribe(
      () => {
        this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationSentAutoRefresh');
this.emailVerificationLoading = false;
      },
      (res) => {
        if (this.isAlreadyVerifiedResponse(res)) {
          this.refreshCurrentPlayerAfterEmailVerification();
          return;
        }

        this.emailVerificationError = this.translate.instant('notifications.emailVerificationSendFailed');
this.emailVerificationLoading = false;
      }
    );
  }

  public checkEmailVerification(showPendingError: boolean = true): void {
    if (this.emailVerificationCheckLoading) {
      return;
    }

    this.emailVerificationCheckLoading = true;
    if (showPendingError) {
      this.emailVerificationError = null;
    }
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => {
        this.currentPlayer = player;
this.playerService.setCurrentPlayer(player);
this.applyEmailVerificationState(player);
if (this.isPlayerEmailUnverified(player) || this.authEmailVerified === false) {
  this.emailVerificationMessage = null;
  if (showPendingError) {
    this.emailVerificationError = this.translate.instant('notifications.emailVerificationPending');
  }
} else if (this.emailVerificationConfirmed) {
  this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationConfirmed');
  this.emailVerificationError = null;
}
this.emailVerificationCheckLoading = false;
      },
      () => {
        if (showPendingError) {
  this.emailVerificationError = this.translate.instant('notifications.emailVerificationCheckFailed');
}
this.emailVerificationCheckLoading = false;
      }
    );
  }

  private refreshEmailVerificationStatus(): void {
    if (this.isEmailVerificationRedirect() || this.shouldShowEmailVerificationReminder()) {
      this.checkEmailVerification(false);
    }
  }

  private isEmailVerificationRedirect(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get("success") === "true" && params.get("code") === "success";
  }

  public remindRatingReviewLater(review: PendingRatingReview): void {
    if (!review || this.ratingReviewLoading) {
      return;
    }

    this.ratingReviewLoading = true;
    this.ratingReviewErrorMsg = null;
    this.playerService.remindRatingReviewLater(review.id).subscribe(
      () => {
        this.pendingRatingReviews = this.pendingRatingReviews.filter((pendingReview) => pendingReview.id !== review.id);
        this.ratingReviewLoading = false;
      },
      (error) => {
        this.ratingReviewErrorMsg = this.getRatingReviewErrorMessage(error, this.translate.instant('notifications.ratingReviewReminderFailed'));
        this.handleRatingReviewAuthError(error);
        this.ratingReviewLoading = false;
      }
    );
  }

  private getCurrentCityAndLocations(): void {
    this.playSpaceService.getCurrentLocations().subscribe((playspaces) => {
      localStorage.setItem("nearByLocations", JSON.stringify(playspaces));
      this.currentPlayerCity = this.getCurrentPlayerCity();
    });
  }

  private refreshCurrentPlayerAndLoadEvents(): void {
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => {
        this.applyCurrentPlayerProfile(player);
        this.loadEventListScreenData();
      },
      () => this.loadEventListScreenData()
    );
  }

  private refreshCurrentPlayer(): void {
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => this.applyCurrentPlayerProfile(player),
      () => {}
    );
  }

  private applyCurrentPlayerProfile(player: Player): void {
    this.currentPlayer = player;
this.currentPlayerCity = this.getCurrentPlayerCity();
this.applyEmailVerificationState(player);
  }

  private loadEventListScreenData(): void {
    this.getEventList();
    this.getCurrentCityAndLocations();
    this.loadPendingRatingReviews();
    this.refreshEmailVerificationStatus();
  }

  private getCurrentPlayerCity(): string {
    if (!this.currentPlayer || !this.currentPlayer.city) {
      return "";
    }

    const separatorIndex = this.currentPlayer.city.indexOf(",");
    return separatorIndex > -1 ? this.currentPlayer.city.slice(0, separatorIndex) : this.currentPlayer.city;
  }

  private loadPendingRatingReviews(): void {
    this.ratingReviewErrorMsg = null;
    this.playerService.getPendingRatingReviews().subscribe(
      (reviews) => {
        this.pendingRatingReviews = reviews || [];
      },
      (error) => {
        this.ratingReviewErrorMsg = this.getRatingReviewErrorMessage(error, this.translate.instant('ratingReview.errors.loadFailed'));
        this.handleRatingReviewAuthError(error);
      }
    );
  }

  public onHomeRealtime({type, data}: {type: string; data: any}): void {
    if (this.destroyed) return;
    if (['chat.message', 'manager.announcement.changed'].includes(type)) {
      const chat = this.chats.find(item => item.id === data?.chatId);
      if (chat && (type === 'manager.announcement.changed' || data?.message?.id)) {
        this.chatRevision++;
        this.chats = this.chats.map(item => item === chat
          ? this.chatService.applyRealtimeUpdate(chat, type, data, this.currentPlayer?.id) : item);
        return;
      }
    }
    if (['connected', 'participants.changed', 'event.updated', 'event.deleted', 'message.created',
      'chat.message', 'chat.updated', 'manager.announcement.changed'].includes(type)) this.queueHomeRefresh();
  }

  private queueHomeRefresh(): void {
    if (this.destroyed) return;
    this.homeRefreshPending = true;
    clearTimeout(this.homeRefreshTimer);
    this.homeRefreshTimer = setTimeout(() => {
      if (!this.destroyed && !this.loading && !this.listReading && !this.attendanceSaving && !this.teamSaving) {
        this.homeRefreshPending = false;
        this.getEventList(false);
      }
    }, 200);
  }

  private getEventList(isLoading: boolean = true, isJoinOrLeave: boolean = false): void {
    let me = this;

    if (isLoading) {
      me.loading = true;
      me.eventListReady$.next(false);
      me.showLoadingSpinner(false);
    }

    const usableLocation = this.getUsablePlayerLocation();
    this.discoveryLocationMissing = !usableLocation;
    const myEventsRequest = me.eventService.getMyHomeEvents();
    const geoEventsRequest = usableLocation
      ? me.eventService.getNearbyHomeEvents(usableLocation.latitude, usableLocation.longitude)
      : of([]);

    this.listSubscription?.unsubscribe();
    this.listReading = true;
    const playerId = this.currentPlayer?.id;
    const chatRevision = this.chatRevision;
    this.listSubscription = forkJoin([myEventsRequest, geoEventsRequest, this.chatService.getChatsForPlayer(playerId)]).subscribe(
      ([myEvents, geoEvents, chats]) => {
        this.listReading = false;
        if (this.destroyed) return;
        if (playerId !== this.playerService.getCurrentPlayer()?.id) { this.getEventList(); return; }
        this.listError = false;
        // A response started before a live message must not roll its preview back.
        if (chatRevision === this.chatRevision) this.chats = chats;
        else this.homeRefreshPending = true;
        me.enrichAndApplyEvents(me.mergeEvents(myEvents, geoEvents), myEvents, geoEvents, isJoinOrLeave);
      },
      () => { this.listReading = false; this.listError = true; me.finishEventListLoading(); }
    );
  }

  private enrichAndApplyEvents(events: Event[], myEventsFromBackend: Event[], discoveryEvents: Event[], isJoinOrLeave: boolean): void {
    if (!events || events.length === 0) {
      this.allEvents = [];
      this.myEvents = [];
      this.sosEvents = [];
      this.events = [];
      this.selectedEvent = null;
      this.finishEventListLoading();
      return;
    }

    this.checkIfPlayerIsManager(events);
    events.forEach((event) => {
      event.originalEventId = event.originalEventId ? event.originalEventId : event.id;
      this.applyCurrentPlayerEventState(event);
      if (event.confirmedCount === undefined) {
        event.confirmedCount = this.eventService.getConfirmedCount(event);
      }
    });

    if (events.length === 1) {
      this.selectedEvent = events[0];
      this.editSelectedEvent = true;

      if (this.currentPlayer && this.selectedEvent.manager && this.selectedEvent.manager.id === this.currentPlayer.id) {
        this.selectedEvent.viewedByManager = true;
        this.editSelectedEvent = false;
      }
    }

    const discoveryEventIds = this.eventIds(discoveryEvents);
    const myEventIds = this.eventIds(myEventsFromBackend);
    const isMember = (event: Event): boolean => {
      const type = event.participation?.teamMemberType;
      return event.manager?.id === this.currentPlayer?.id ||
        (type !== 'CANDIDATE' && (event.participates === true || !!type || event.viewedByPlayer));
    };
    const discovery = events.filter(event => !!discoveryEventIds[event.id] && this.isActiveEvent(event));
    const open = discovery.filter(event => event.openToPublic !== false);
    this.myEvents = this.sortHomeEvents(events.filter(event => this.isActiveEvent(event) &&
      (!!myEventIds[event.id] || (event.openToPublic !== false && isMember(event)))));
    // The mobile home groups organizing before playing, retaining chronological order within each group.
    this.myEvents = this.myEvents.filter(event => event.viewedByManager)
      .concat(this.myEvents.filter(event => !event.viewedByManager && !event.viewedByCandidate))
      .concat(this.myEvents.filter(event => !event.viewedByManager && event.viewedByCandidate));
    this.allEvents = this.sortHomeEvents(open.filter(event => !isMember(event)));
    this.sosEvents = this.sortHomeEvents(discovery.filter(event => !isMember(event) &&
      (event.sosUsed === true || event.teammateSosUsed === true)));
    this.events = this.isSosMatchesClicked ? this.sosEvents : this.isAllMatchesClicked ? this.allEvents : this.myEvents;

    this.finishEventListLoading();
    this.selectedEvent = null;
  }

  private sortHomeEvents(events: Event[]): Event[] {
    const time = (event: Event): number => {
      const value = event.nextOccurrenceTimestamp || event.nextOccurrence;
      const timestamp = value ? new Date(value).getTime() : NaN;
      return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
    };
    return [...events].sort((left, right) => time(left) - time(right));
  }

  public mySectionTitle(index: number): string {
    if (!this.isMyMatchesClicked) return '';
    const section = (event: Event): string => event.viewedByManager ? 'events.organizingSectionTitle'
      : event.viewedByCandidate ? 'events.pendingRequestsSectionTitle' : 'events.playingSectionTitle';
    const current = this.events[index];
    if (!current) return '';
    return index === 0 || section(this.events[index - 1]) !== section(current) ? section(current) : '';
  }

  public mySectionCount(index: number): number {
    const current = this.events[index];
    return this.events.filter(event => event.viewedByManager === current.viewedByManager &&
      (event.viewedByManager || !!event.viewedByCandidate === !!current.viewedByCandidate)).length;
  }

  private applyCurrentPlayerEventState(event: Event): void {
    if (!event || !event.playerEventInfos) {
      return;
    }

    for (let playerEventInfo of event.playerEventInfos) {
      if (playerEventInfo.teamMemberType === this.teamMemberType.CANDIDATE) {
        event.newCandidatesFound = true;

        if (this.currentPlayer && playerEventInfo.player && playerEventInfo.player.id === this.currentPlayer.id) {
          event.viewedByCandidate = true;
        }
      }

      if (this.currentPlayer && playerEventInfo.player && playerEventInfo.player.id === this.currentPlayer.id) {
        event.currentPlayerInviteStatus = playerEventInfo.status;
        if (playerEventInfo.teamMemberType !== this.teamMemberType.CANDIDATE) {
          event.viewedByPlayer = true;
        }
      }
    }
  }

  private mergeEvents(myEvents: Event[], geoEvents: Event[]): Event[] {
    const byId: {[eventId: string]: Event} = {};
    (geoEvents || []).concat(myEvents || []).forEach((event) => {
      if (event && event.id) {
        byId[event.id] = Object.assign({}, byId[event.id] || {}, event);
      }
    });
    return Object.keys(byId).map((eventId) => byId[eventId]);
  }

  private eventIds(events: Event[]): {[eventId: string]: boolean} {
    const ids: {[eventId: string]: boolean} = {};
    (events || []).forEach((event) => {
      if (event && event.id) {
        ids[event.id] = true;
      }
    });
    return ids;
  }

  private getUsablePlayerLocation(): {latitude: number, longitude: number} {
    if (!this.currentPlayer?.city?.trim()) return null;
    if (this.currentPlayer && this.currentPlayer.location && this.currentPlayer.location.coordinates) {
      const coordinates: any = this.currentPlayer.location.coordinates;
      if (this.isFiniteNumber(coordinates[0]) && this.isFiniteNumber(coordinates[1])) {
        return { latitude: Number(coordinates[0]), longitude: Number(coordinates[1]) };
      }
    }

    if (this.currentPlayer && this.isFiniteNumber(this.currentPlayer.addressLat) && this.isFiniteNumber(this.currentPlayer.addressLong)) {
      return { latitude: Number(this.currentPlayer.addressLat), longitude: Number(this.currentPlayer.addressLong) };
    }

    return null;
  }

  private isFiniteNumber(value: any): boolean {
    return value !== null && value !== undefined && value !== "" && isFinite(Number(value));
  }

  private isActiveEvent(event: Event): boolean {
    return event && event.active !== false;
  }

  public eventDateLabel(event: Event): string {
    return event && (event.nextOccurrenceTimestamp || event.nextOccurrence) ? "" : this.translate.instant('events.timeNotSet');
  }

  public eventLocationLabel(event: Event): string {
    return event && event.playSpace && event.playSpace.name
      ? event.playSpace.name
      : (event && event.locationName ? event.locationName : this.translate.instant('events.locationTbd'));
  }

  private finishEventListLoading(): void {
    this.loading = false;
    this.hideLoadingSpinner();
    this.eventListReady$.next(true);
    if (this.homeRefreshPending) this.queueHomeRefresh();
  }

  private showLoadingSpinner(useOverlay: boolean = true): void {
    this.clearLoadingSpinnerTimeout();
    this.showDelayedLoadingState = false;
    this.loadingSpinnerTimeout = setTimeout(() => {
      if (this.loading) {
        if (useOverlay) {
          this.spinner.show();
        } else {
          this.showDelayedLoadingState = true;
        }
      }
      this.loadingSpinnerTimeout = null;
    }, this.loadingSpinnerDelayMs);
  }

  private hideLoadingSpinner(): void {
    this.clearLoadingSpinnerTimeout();
    this.showDelayedLoadingState = false;
    this.spinner.hide();
  }

  private clearLoadingSpinnerTimeout(): void {
    if (!this.loadingSpinnerTimeout) {
      return;
    }

    clearTimeout(this.loadingSpinnerTimeout);
    this.loadingSpinnerTimeout = null;
  }

  private sortEvents(events: Set<Event>): Event[] {
    let managerEventList: Event[] = Array<Event>();
    let playerEventList: Event[] = Array<Event>();
    let otherEventList: Event[] = Array<Event>();

    events.forEach(function (event) {
      if (event.viewedByManager) {
        managerEventList.push(event);
      } else if (event.viewedByPlayer) {
        playerEventList.push(event);
      } else {
        otherEventList.push(event);
      }
    });

    let eventList = managerEventList.concat(playerEventList);
    eventList = eventList.concat(otherEventList);

    return eventList;
  }

  private checkIfPlayerIsManager(events: Event[]): void {
    this.isCurrentPlayerManager = false;

    if (events) {
      for (let event of events) {
        event.viewedByManager = false;

        if (this.currentPlayer && event.manager && event.manager.id === this.currentPlayer.id) {
          this.isCurrentPlayerManager = true;
          event.viewedByManager = true;
        }
      }
    }
  }

  public filterEvents(value: string): void {
    let me: any = this;
    if (value === "allMatches") {
      me.isMyMatchesClicked = false;
      me.isSosMatchesClicked = false;
      me.isAllMatchesClicked = true;
      localStorage.setItem("eventsFilter", "allMatches");
      me.events = me.allEvents;
    } else if (value === "myMatches") {
      me.isAllMatchesClicked = false;
      me.isSosMatchesClicked = false;
      me.isMyMatchesClicked = true;
      localStorage.setItem("eventsFilter", "myMatches");
      me.events = me.myEvents;
    } else {
      me.isMyMatchesClicked = false;
      me.isAllMatchesClicked = false;
      me.isSosMatchesClicked = true;
      localStorage.setItem("eventsFilter", "sosMatches");
      me.events = me.sosEvents;
    }
  }

  private handleRatingReviewAuthError(error: any): void {
    if (error && (error.status === 401 || error.status === 403)) {
      this.authRedirect.login().subscribe();
    }
  }

  private getRatingReviewErrorMessage(response: any, fallback: string): string {
    if (response && response.error) {
      if (response.error.message) {
        return response.error.message;
      }
      if (response.error.errorMsg) {
        return response.error.errorMsg;
      }
      if (typeof response.error === "string") {
        return response.error;
      }
    }

    return fallback;
  }

  private refreshCurrentPlayerAfterEmailVerification(): void {
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => {
        this.currentPlayer = player;
this.playerService.setCurrentPlayer(player);
this.applyEmailVerificationState(player);
if (this.emailVerificationConfirmed) {
  this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationAlreadyConfirmed');
  this.emailVerificationError = null;
}
this.emailVerificationLoading = false;
      },
      () => {
        this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationAlreadyConfirmedReload');
this.emailVerificationError = null;
this.emailVerificationLoading = false;
      }
    );
  }

  private applyEmailVerificationState(player: Player): void {
    if (this.isPlayerEmailVerified(player)) {
      this.authEmailVerified = true;
      this.emailVerificationConfirmed = true;
      this.emailVerificationError = null;
      return;
    }

    if (this.isPlayerEmailUnverified(player)) {
      this.authEmailVerified = false;
      this.emailVerificationConfirmed = false;
      if (this.emailVerificationConfirmed) {
        this.emailVerificationMessage = null;
      }
    }
  }

  private isPlayerEmailVerified(player: Player): boolean {
    return !!player && (player.emailVerified === true || player.email_verified === true);
  }

  private isPlayerEmailUnverified(player: Player): boolean {
    return !!player && (player.emailVerified === false || player.email_verified === false);
  }

  private isAlreadyVerifiedResponse(res: any): boolean {
    const backendMessage = this.getBackendMessage(res);
    return backendMessage.indexOf("ALREADY_VERIFIED") > -1 || backendMessage.indexOf("EMAIL_ALREADY_VERIFIED") > -1 || backendMessage.indexOf("already verified") > -1;
  }

  private getBackendMessage(res: any): string {
    if (!res || !res.error) {
      return "";
    }

    return (res.error.errorMsg || res.error.message || res.error.error || res.error || "").toString();
  }
}
